import { useAppSelector } from "@/hooks/redux";
import { getDashBoardState } from "@/redux/store";
import { Button, Input, Typography, Form, notification, MenuProps } from "antd";
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { CustomDropdownButton, CustomDropdownContainer, CustomDropdownItem, CustomDropdownItemContainer, CustomInviteWorkspaceModal } from "../CreateWorkspace/Style";
import { Trans, useTranslation } from "next-i18next";
import { checkIsExistMember, checkIsExistSendInvitation, checkIsExistSubscription, handleInviteMemberRenewal, handleRequestMemberWorkspace } from "@/lib/workspace-util";
import { CheckOutlined, DownOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { IconWorkspaceHyperlink } from "@/components/common/Icons";
import CryptoJS from 'crypto-js';
import { useCopyToClipboard } from "react-use";
import { MemberType } from "@/types/dashboard.api";
import { uniq } from "lodash";
import { WORKSPACE_EMAIL_TEMPLATE, WORKSPACE_USER_TYPE, WorkspaceUserType } from "@/constants/dashboard/workspace";
import { useUserWorkspace } from "@/hooks/dashboard/useDashboard";
import { CheckIsExistMemberRes, CheckIsExistSendInvitationRes, CheckIsExistSubscriptionRes } from "@/types/workspace";

const TAG = '[InviteWorkspaceModal]';

export default function InviteWorkspaceModal(
  {
    setShowModal,
    showModal
  }: {
    setShowModal: Dispatch<SetStateAction<boolean>>,
    showModal: boolean,
  }
) {
  const { accountUserInfo, workspaceList, selectedWorkspaceIndex } = useAppSelector((store) => getDashBoardState(store) ?? false );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inviteMembers, setInviteMembers] = useState<MemberType[]>([]);
  const [email, setEmail] = useState<string>('');
  const [userType, setUserType] = useState<WorkspaceUserType>(WORKSPACE_USER_TYPE.MEMBER);
  const [renderUserType, setRenderUserType] = useState<WorkspaceUserType>(WORKSPACE_USER_TYPE.MEMBER);

  const [, copyToClipboard] = useCopyToClipboard();

  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const { t } = useTranslation('common');
  const { currentWorkspace } = useUserWorkspace();

  const isEmailValid = useCallback((input: string) => /^[^@]+@[^@]+\.[^@]+$/.test(input), []);

  const isEnterpriseUser = useMemo(() => {
    if(accountUserInfo?.enterprise || accountUserInfo?.plan?.toLocaleLowerCase()?.includes('enterprise')) {
      return true;
    } 
    return false;
  }, [accountUserInfo?.enterprise, accountUserInfo?.plan])

  const userTypeItems: MenuProps['items'] = useMemo(() => [
    {
      label: t(WORKSPACE_USER_TYPE.MEMBER),
      key: WORKSPACE_USER_TYPE.MEMBER,
    },
    {
      label: t(WORKSPACE_USER_TYPE.ADMIN),
      key: WORKSPACE_USER_TYPE.ADMIN,
    }
  ], [t]);

  const renderInviteMembers = useMemo(() => {
    /**
     * 4좌석 이상일 경우는 우선 최대치로 무조건 3개만 보여준다 
     */
    const currentAvailableSeats = currentWorkspace?.availableSeats || 1;
    const availableSeats = currentAvailableSeats > 4 ? 3 : currentAvailableSeats -1;
    const registerForm = (
      <div className='form-content'>
        <div className='input-wrapper'>
          <Form.Item
          className='input-email'
          name="email2"
          >
            <Input
            type='email'
            placeholder={t("Enter email address...")}
            bordered={false}
            allowClear
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = event?.target?.value;
              setInviteMembers(inviteMembers.map((val) => {
                if(val.email.toLocaleLowerCase()?.includes(value)) {
                  return { ...val, userType}
                }
                return val;
              }))
            }}/>
          </Form.Item>
          <Form.Item
          className='select-type'
          name='userType2'>
          <CustomDropdownButton
          menu={{
            items: userTypeItems,
          }}
          icon={<DownOutlined />}
          overlayStyle={{
            border: 'none',
            borderColor: 'transparent',
            boxShadow: '0px'
          }}
          style={{ 
          border: 'none', 
          borderColor: '#ffffff',
          boxShadow: '0px'}}
          destroyPopupOnHide
          dropdownRender={() => (
            <CustomDropdownContainer>
              <CustomDropdownItemContainer
              $selected={renderUserType === WORKSPACE_USER_TYPE.ADMIN}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                setRenderUserType(WORKSPACE_USER_TYPE.ADMIN)
                form.setFieldsValue({ userType2: WORKSPACE_USER_TYPE.ADMIN })
              }}
              >
                <CustomDropdownItem>
                  <span style={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px'}}>{t(WORKSPACE_USER_TYPE.ADMIN)}</span>
                  {renderUserType === WORKSPACE_USER_TYPE.ADMIN && <CheckOutlined color="#000000" style={{ fontWeight: 'semi-bold' }}/>} 
                </CustomDropdownItem>
                <span>{t(`Can manage the entire workspace, its project and video`)}</span>
              </CustomDropdownItemContainer>
              <CustomDropdownItemContainer
              $selected={renderUserType === WORKSPACE_USER_TYPE.MEMBER}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                setRenderUserType(WORKSPACE_USER_TYPE.MEMBER)
                form.setFieldsValue({ userType2: WORKSPACE_USER_TYPE.MEMBER })
              }}>
                <CustomDropdownItem>
                  <span style={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px'}}>{t(WORKSPACE_USER_TYPE.MEMBER)}</span>
                  {renderUserType === WORKSPACE_USER_TYPE.MEMBER && <CheckOutlined color="#000000" style={{ fontWeight: 'semi-bold' }}/>} 
                </CustomDropdownItem>   
                <span>{t(`Can access project and video in that workspace`)}</span>
              </CustomDropdownItemContainer>
            </CustomDropdownContainer>
          )}
          >
            {renderUserType}
          </CustomDropdownButton>
          </Form.Item>
        </div>
      </div>
    )

    const inputs = Array.from({ length: availableSeats }, () => registerForm);
    return inputs;
  }, [currentWorkspace?.availableSeats, form, inviteMembers, renderUserType, t, userType, userTypeItems]);

  const showErrorNotification = useCallback((errorMessage: string) => {
    api.error({
      message: <span style={{ color: '#ffffff' }}>{t(errorMessage)}</span>,
      duration: 3,
      placement: 'top',
      style: {
        width: '700px',
        backgroundColor: 'red',
        position: 'relative',
        borderRadius: '8px',
        padding: '13px 30px',
      },
      closeIcon: <span style={{ color: '#ffffff' }}>X</span>,
      icon: <ExclamationCircleOutlined style={{ color: '#ffffff' }} />,
    });
  }, [api, t])

  const handleInviteMember = useCallback(async() => {
    try {
      if (window?.dataLayer?.push) {
        window.dataLayer.push({
          event: "invite_members",
        });
      }
      setIsLoading(true);

      const members: MemberType[] = currentWorkspace.userType === WORKSPACE_USER_TYPE.MEMBER
      ? [
          {
            email: form.getFieldValue('email'),
            userType: WORKSPACE_USER_TYPE.MEMBER,
          },
        ]
      : [
          { 
            email: form.getFieldValue('email'), 
            userType: form.getFieldValue('userType'),
          }, 
          { 
            email: form.getFieldValue('email2'), 
            userType: form.getFieldValue('userType2'),
          },
        ];

      /**
       * email 정규식 부합한것만 가져와서 처리한다.
       * - 정규식 통과못하면 진행 하지마셈!!
       */
      const filteredMembers = members?.filter((val) => val.email !== undefined) ?? [];

      if(filteredMembers.length === 0) {
        alert(`${t(`Please insert invite email`)}`)
        setIsLoading(false);
        return;
      }

      const invalidEmail = filteredMembers.find((val) => !isEmailValid(val.email));
      if(invalidEmail) {
        const formValue = form.getFieldsValue();
        const sameEmail = Object.keys(formValue).find(key => formValue[key] === invalidEmail.email);
        form.setFields([
          {
            name: sameEmail,
            errors: [t(`It doesn't look like an email address.`)],
          },
        ]);
        setIsLoading(false);
        return;
      }

      /**
       * 두개가 있으면 두개가 같은 이메일인지 비교한다 
       */
      if(filteredMembers.length > 1) {
        const emailList: string[] = filteredMembers.map(member => member.email);
        const hasSameEmail = uniq(emailList).length === 1;

        if(hasSameEmail) {
          const formValue = form.getFieldsValue();
          const duplicateEmail = Object.keys(formValue).reverse().find(key => formValue[key] === emailList[0]);
          form.setFields([
            {
              name: duplicateEmail,
              errors: [t(`Duplicate Email. Please insert again.`)],
            },
          ]);
          setIsLoading(false);
          return;
        }
      }
      
      /**
       * 1. email 링크 유효한지 체크함
       * -> Test Data
       * -> workspaceId: "6720898939230a9675864fcc"
       * -> companyId: "671760934604e8079b51f9ee"
       */
      const { data: checkIsExistSendInvitationResult }: 
      { data: CheckIsExistSendInvitationRes } = 
      await checkIsExistSendInvitation({
        inviteMembers: filteredMembers,
        workspaceId: currentWorkspace.workspaceId,
      })
      if(
        !checkIsExistSendInvitationResult.success ||
        checkIsExistSendInvitationResult.data.length > 0
      ) {
        if(!checkIsExistSendInvitationResult.success) {
          showErrorNotification(`An error occurred while sending the invitation in the first step. Please try again.`);
          form.resetFields();
          setIsLoading(false);
          return;
        }
        /**
         * 이메일 보낸지 하루 지났는지 체크
         */
        const inviteLogCreatedAt = new Date(checkIsExistSendInvitationResult.data[0].createdAt);
        const now = new Date();
        if(now.getTime() - inviteLogCreatedAt.getTime() < 24 * 60 * 60 * 1000) {
          form.setFields([
            {
              name: 'email',
              errors: [t('The email link has already been sent.')],
            }
          ])
          setIsLoading(false);
          return;
        }
      }

      /**
       * 2. 해당 워크스페이스 내 가입 유무 체크
       */
      const { data: checkIsExistMemberResult }: 
      { data: CheckIsExistMemberRes } = 
      await checkIsExistMember({
        inviteMembers: filteredMembers,
        workspaceId: currentWorkspace.workspaceId,
      });
      if(
        checkIsExistMemberResult &&
        (
          !checkIsExistMemberResult.success ||
          checkIsExistMemberResult.data
        )
      ) {
        if(!checkIsExistMemberResult.success) {
          showErrorNotification(`An error occurred while sending the invitation in the second step. Please try again.`);
          form.resetFields();
          return;
        }
        const matchEmail = checkIsExistMemberResult.data &&
        Object.entries(form.getFieldsValue())
          .find(([, value]) => 
            typeof value === 'string' &&
            value.includes('@') &&
            checkIsExistMemberResult.data?.some(({ userEmail }) => userEmail === value)
          )
          ?.shift() || form.getFieldValue('email');

        form.setFields([
          {
            name: matchEmail,
            errors: [t('Email already joined.')],
          }
        ])
        setIsLoading(false);
        return;
      }
      
      /**
       * 3. 구독권을 가지고 있는 유저인지 체크 
       */
      const { data: checkIsExistSubscriptionResult }: 
      { data: CheckIsExistSubscriptionRes } = 
      await checkIsExistSubscription({ inviteMembers: filteredMembers });
      if(
        checkIsExistSubscriptionResult &&
        (
          !checkIsExistSubscriptionResult.success ||
          checkIsExistSubscriptionResult.data
        )
      ) {
        if(!checkIsExistSubscriptionResult.success) {
          showErrorNotification(`An error occurred while sending the invitation in the third step. Please try again.`);
          form.resetFields();
          return;
        }

        const matchEmail = checkIsExistSubscriptionResult.data?.id
        ? Object.entries(form.getFieldsValue())
        .find(([, value]) => value === checkIsExistSubscriptionResult.data?.id)?.[0]
        : 'email';

        form.setFields([
          {
            name: matchEmail,
            errors: [t('Already have subscription.')],
          }
        ])
        setIsLoading(false);
        return;
      }

      const query = {
        inviteMembers: currentWorkspace.userType === WORKSPACE_USER_TYPE.MEMBER ? filteredMembers[0] : filteredMembers,
        templateName: currentWorkspace.userType === WORKSPACE_USER_TYPE.MEMBER ? WORKSPACE_EMAIL_TEMPLATE.INVITE_WORKSPACE_MEMBER : WORKSPACE_EMAIL_TEMPLATE.INVITE_WORKSPACE,
        invitedBy: accountUserInfo?.id,
        companyId: workspaceList[selectedWorkspaceIndex].companyId,
        workspaceId: workspaceList[selectedWorkspaceIndex].workspaceId,
        workspaceName: workspaceList[selectedWorkspaceIndex].workspaceName,
      }

      const handleInviteMemberFunction = currentWorkspace.userType === WORKSPACE_USER_TYPE.MEMBER
      ? handleRequestMemberWorkspace
      : handleInviteMemberRenewal;

      await handleInviteMemberFunction(query) 
      .then((result) => {
        if(result?.success) {
          setIsLoading(false);
          setShowModal(false);
          const successMessage = currentWorkspace.userType === WORKSPACE_USER_TYPE.MEMBER
          ? 'Your invitation request has been successfully sent to administrator.'
          : 'Your invitation email has been sent successfully.';
          notification.success({
            message: <span style={{ color: '#ffffff'}}>{t(successMessage)}</span>,
            duration: 4,
            placement: 'top',
            style: {
              width: '700px',
              backgroundColor: '#2bab62',
              position: 'relative',
              borderRadius: '8px',
              padding: '13px 30px',
            },
            closeIcon: <span style={{ color: '#ffffff' }}>X</span>, 
            icon: <ExclamationCircleOutlined style={{ color: '#ffffff' }} />,
          });
        }
      }).catch((error) => {
        console.log(TAG, `handleInviteMemberRenewal error `, {error});
        const errorMessage = error?.message || 'Invite Member error. Please try again';
        setShowModal(false);
        setIsLoading(false);
        alert(t(`${errorMessage}`));
      })

    } catch(error) {
      console.error(TAG, 'handleInviteMember error ', {error});
      setShowModal(false);
      setIsLoading(false);
      const errorMessage = error?.message || 'Invite send email error. Please try again.'
      alert(t(`Invite send email error. ${errorMessage}`));
    } finally {
      setIsLoading(false);
    }
  }, [accountUserInfo?.id, currentWorkspace.userType, currentWorkspace.workspaceId, form, isEmailValid, selectedWorkspaceIndex, setShowModal, showErrorNotification, t, workspaceList])

  const handleCopyLink = useCallback(() => {
    const originParams = [
      {
        workspaceId: workspaceList[selectedWorkspaceIndex].workspaceId,
      },
      {
        companyId: workspaceList[selectedWorkspaceIndex].companyId,
      },
      {
        invitedBy: accountUserInfo?.id,
      },
      {
        workspaceName: workspaceList[selectedWorkspaceIndex].workspaceName,
      }
    ];
    const params = CryptoJS.AES.encrypt(JSON.stringify(originParams), 'copy-invitation-link').toString();
    const url = `${window.location.origin}/workspace/invite-copy-render?inviteCopyLink=${encodeURIComponent(params)}`;

    copyToClipboard(url);
    notification.success({
      message: <span style={{ color: '#ffffff'}}>{t('Invite link copied to clipboard. Everyone with link can access.')}</span>,
      duration: 4,
      placement: 'top',
      style: {
        width: '700px',
        backgroundColor: '#2bab62',
        position: 'relative',
        borderRadius: '8px',
        padding: '13px 30px',
      },
      closeIcon: <span style={{ color: '#ffffff' }}>X</span>, 
      icon: <ExclamationCircleOutlined style={{ color: '#ffffff' }} />,
    });
  }, [accountUserInfo?.id, copyToClipboard, selectedWorkspaceIndex, t, workspaceList])

  return (
    <CustomInviteWorkspaceModal
    width={400}
    open={showModal}
    centered
    closable
    footer={null}
    onCancel={() => {
      setShowModal(false);
    }}
    title={<Typography.Title>{t(`Invite Workspace Members`)}</Typography.Title>}
    >
      <>
      {contextHolder}
      <div className='content'>
        {
          currentWorkspace.userType === WORKSPACE_USER_TYPE.MEMBER
          ? (
            <span
            style={{
              color: '#000000',
              textDecoration: 'normal',
            }}
            >
            {t(`Send out invitations now, and we'll forward an approval request to your team's administrator. An invitation email will be sent after administrator approval.`)}
            </span>
          )
          : (
            <>
            {
              isEnterpriseUser
              ? (
                <Trans
                i18nKey={`Your team has <color>{{seats}}</color> people`}
                components={{
                  color: <span style={{ color: '#3075f6'}}/>
                }}
                values={{
                  seats: currentWorkspace?.seats?.length || 0,
                }}
                />
              )
              : (
                <Trans
                i18nKey={`Your team has <color>{{occupiedSeats}}</color> people, <color>{{seats}}</color> seats available.`}
                components={{
                  color: <span style={{ color: '#3075f6'}}/>
                }}
                values={{
                  occupiedSeats: currentWorkspace?.seats?.length || 1,
                  seats: currentWorkspace?.availableSeats || 0,
                }}
                />
              )
            }
            </>
          )
        }
      </div>
      {
        currentWorkspace.userType === WORKSPACE_USER_TYPE.MEMBER
        ? (
          <Form
          form={form}
          name="register"
          autoComplete="off"
          scrollToFirstError
          >
            <div className="form-content">
              <div className="input-wrapper">
                <Form.Item
                className="input-email"
                name="email"
                rules={[
                  {
                    type: 'email',
                    message: t(`It doesn't look like an email address`),
                  },
                  {
                    required: true,
                    message: t('Please input your E-mail'),
                  },
                ]}
                >
                  <Input
                  type="email"
                  value={email}
                  placeholder={t('Enter email address...')}
                  bordered={false}
                  onChange={(event) => {
                    const value = event?.target?.value;
                    setEmail(value);
                  }}
                  allowClear
                  onPressEnter={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    if(inviteMembers.length > 0) {
                      setInviteMembers((prev) => [{ email, userType}, ...prev])
                    } else {
                      setInviteMembers([{ email, userType }])
                    }
                  }}
                  />
                </Form.Item>
              </div>
            </div>
          </Form>
        )
        : (
          <Form
          form={form}
          name="register"
          autoComplete="off"
          initialValues={{ 
            userType,
            userType2: renderUserType,
          }}
          scrollToFirstError
          >
            <div className="form-content">
              <div className="input-wrapper">
                <Form.Item
                className="input-email"
                name="email"
                rules={[
                  {
                    type: 'email',
                    message: t(`It doesn't look like an email address`),
                  },
                  {
                    required: true,
                    message: t('Please input your E-mail'),
                  },
                ]}
                >
                  <Input
                  type="email"
                  value={email}
                  placeholder={t('Enter email address...')}
                  bordered={false}
                  onChange={(event) => setEmail(event?.target?.value)}
                  allowClear
                  onPressEnter={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    if(inviteMembers.length > 0) {
                      setInviteMembers((prev) => [{ email, userType}, ...prev])
                    } else {
                      setInviteMembers([{ email, userType }])
                    }
                  }}
                  />
                </Form.Item>
                <Form.Item
                className="select-type"
                name="userType"
                >
                  <CustomDropdownButton
                  menu={{
                    items: userTypeItems,
                  }}
                  icon={<DownOutlined/>}
                  overlayStyle={{
                    border: 'none',
                    borderColor: 'transparent',
                    boxShadow: '0px',
                  }}
                  style={{
                    border: 'none',
                    borderColor: '#ffffff',
                    boxShadow: '0px',
                  }}
                  destroyPopupOnHide
                  dropdownRender={() => (
                    <CustomDropdownContainer>
                      <CustomDropdownItemContainer
                      $selected={userType === WORKSPACE_USER_TYPE.ADMIN}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        setUserType(WORKSPACE_USER_TYPE.ADMIN)
                        form.setFieldsValue({ userType: WORKSPACE_USER_TYPE.ADMIN });
                      }}
                      >
                        <CustomDropdownItem>
                          <span>{t(WORKSPACE_USER_TYPE.ADMIN)}</span>
                          {userType === WORKSPACE_USER_TYPE.ADMIN && <CheckOutlined color="#000000" style={{ fontWeight: 'semi-bold'}}/>}
                        </CustomDropdownItem>
                        <span>{t(`Can manage the entire workspace, its project and video`)}</span>
                      </CustomDropdownItemContainer>
                      <CustomDropdownItemContainer
                      $selected={userType === WORKSPACE_USER_TYPE.MEMBER}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        setUserType(WORKSPACE_USER_TYPE.MEMBER);
                        form.setFieldsValue({ userType: WORKSPACE_USER_TYPE.MEMBER });
                      }}
                      >
                        <CustomDropdownItem>
                          <span>{t(WORKSPACE_USER_TYPE.MEMBER)}</span>
                          {userType === WORKSPACE_USER_TYPE.MEMBER && <CheckOutlined color="#000000" style={{ fontWeight: 'semi-bold' }}/>} 
                        </CustomDropdownItem>
                        <span>{t(`Can access project and video in that workspace`)}</span>
                      </CustomDropdownItemContainer>
                    </CustomDropdownContainer>
                  )}
                  >
                    {t(userType)}
                  </CustomDropdownButton>
                </Form.Item>
              </div>
            </div>
            {
              currentWorkspace.userType !== WORKSPACE_USER_TYPE.MEMBER
              && currentWorkspace?.availableSeats > 1 
              && renderInviteMembers
            }
            {/* {currentWorkspace.userType !== WORKSPACE_USER_TYPE.MEMBER && email.length > 0 && renderInviteMembers} */}
          </Form>
        )
      }
       <div className="send-wrapper">
        {
          currentWorkspace.userType !== WORKSPACE_USER_TYPE.MEMBER && 
          isEnterpriseUser &&
          (
            <Button
            className="copy"
            onClick={handleCopyLink}
            >
              <IconWorkspaceHyperlink stroke={'#000000'}/>
              {t('Copy Invitation Link')}
            </Button>
          )
        }
        <Button
        className="send"
        onClick={handleInviteMember}
        disabled={isLoading}
        loading={isLoading}
        >
          {
            currentWorkspace.userType === WORKSPACE_USER_TYPE.MEMBER 
            ? <span>{t('Send Request')}</span> 
            : <span>{t('Send Invitation Email')}</span>
          }
        </Button>
      </div>
      </>
    </CustomInviteWorkspaceModal>
  )
}